// V3 131.10 (Mirko 15. 9. 2026): kontaktné osoby partnera priamo pri e-maile — Matrix ponúkne „komu“, výber osoby
// doplní aj jej telefón a nový kontakt partnera sa dá založiť rovno tu. Jeden zoznam pre celý Matrix (CP, okno
// odoslania dokladu …); zoznam partnera sa načíta raz a zdieľajú ho všetky polia na obrazovke.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { COLORS, TYPO, RADIUS, norm } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from './Toast.jsx'
import TelefonInput from './TelefonInput.jsx'
import { SearchableSelect } from './Ui.jsx'

const lc = (s) => String(s || '').trim().toLowerCase()
export const cislaTel = (s) => String(s || '').replace(/\D/g, '').slice(-9)
const EMAIL_OK = /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/

const pamat = new Map()   // customerId → { rows, subs, loading }
function polozka(cid) {
  let z = pamat.get(cid)
  if (!z) { z = { rows: null, subs: new Set(), loading: null }; pamat.set(cid, z) }
  return z
}
function rozposli(z, rows) { z.rows = rows; z.subs.forEach(fn => { try { fn(rows) } catch (_) {} }) }
function nacitaj(cid) {
  const z = polozka(cid)
  if (z.loading) return z.loading
  z.loading = fetchAuth('/api/partner-contacts?customer_id=' + encodeURIComponent(cid))
    .then(r => (r.ok ? r.json() : null))
    .then(d => { z.loading = null; if (Array.isArray(d)) rozposli(z, d); return z.rows || [] })
    .catch(() => { z.loading = null; return z.rows || [] })
  return z.loading
}

// Kontaktné osoby partnera (zdieľané medzi poľami); pridaj(row) po uložení nového kontaktu obnoví všetky polia naraz
export function usePartnerKontakty(customerId) {
  const cid = String(customerId || '').trim()
  const [rows, setRows] = useState(() => (cid && pamat.has(cid) && pamat.get(cid).rows) || [])
  useEffect(() => {
    if (!cid) { setRows([]); return undefined }
    const z = polozka(cid)
    const fn = (r) => setRows(r)
    z.subs.add(fn)
    setRows(z.rows || [])
    nacitaj(cid)   // pri otvorení vždy čerstvé (kontakty mení aj kolega); súbežné volania sa zlúčia do jedného
    return () => { z.subs.delete(fn) }
  }, [cid])
  const pridaj = useCallback((row) => {
    if (!cid || !row || !row.id) return
    const z = polozka(cid)
    rozposli(z, [...(z.rows || []).filter(x => x.id !== row.id), row])
  }, [cid])
  return { rows, pridaj }
}

// ── p107 — CHRBTICA: celá databáza ľudí Matrixu pre výber osoby, e-mail aj SMS ──────────────────────────────────────
// Mirko 22. 9. 2026: „Podmienka musí byť, že databázu pozná celý Matrix — robím CP alebo objednávku, chcem poslať SMS
// a e-mail, mám možnosť hľadať" a „toto je od začiatku v ústave — jeden celok prepojený" (0.J, 680, 111.1). Jeden zdroj:
// GET /api/kontakty/vyber (skladá sa z registrov, nič sa nekopíruje; súkromné schránky nikdy). Osoby vybraného partnera
// sú navrchu. Rola, ktorá Kontakty nevidí, dostane len register partnera (ako doteraz) a SMS zoznam.
export const pekneTel = (t) => { const d = String(t || '').replace(/\D/g, ''); return d.startsWith('421') && d.length === 12 ? '+421 ' + d.slice(3, 6) + ' ' + d.slice(6, 9) + ' ' + d.slice(9) : (d ? '+' + d : '') }
// číslo vo všetkých tvaroch, ako ho ľudia píšu (0905…, 0905 123 456, +421 905…, 905…) — aby sa našlo každým
const tvaryCisla = (t) => { const d = String(t || '').replace(/\D/g, ''); if (!d) return ''; const n9 = d.slice(-9); return [d, '0' + n9, n9, pekneTel(d), '0' + n9.slice(0, 3) + ' ' + n9.slice(3, 6) + ' ' + n9.slice(6)].join(' ') }

const databaza = new Map()   // partner ('' = bez partnera) → { ludia, at, loading, zakazane, subs }
function nacitajDatabazu(partner) {
  const kl = partner || ''
  let z = databaza.get(kl)
  if (!z) { z = { ludia: null, at: 0, loading: null, zakazane: false, subs: new Set() }; databaza.set(kl, z) }
  if (z.loading) return z.loading
  if (z.ludia && Date.now() - z.at < 60000) return Promise.resolve(z.ludia)
  const rozposli = () => z.subs.forEach(fn => { try { fn(z) } catch (_) {} })
  z.loading = fetchAuth('/api/kontakty/vyber' + (kl ? '?partner=' + encodeURIComponent(kl) : ''))
    .then(r => { if (r.status === 403) { z.zakazane = true; return null } return r.ok ? r.json() : null })
    .then(d => { z.loading = null; if (d && Array.isArray(d.ludia)) { z.ludia = d.ludia; z.at = Date.now() } rozposli(); return z.ludia || [] })
    .catch(() => { z.loading = null; rozposli(); return z.ludia || [] })
  return z.loading
}
// { ludia: [{ k, m meno, f firma, p popis, d druh, t [čísla], e [e-maily], pr 1 osoba partnera | 2 doména partnera | 0 }], zakazane }
export function useKontaktyDatabaza(partner, zapnute = true) {
  const kl = String(partner || '').trim()
  const [stav, setStav] = useState(() => { const z = databaza.get(kl); return { ludia: (z && z.ludia) || [], zakazane: !!(z && z.zakazane) } })
  useEffect(() => {
    if (!zapnute) return undefined
    nacitajDatabazu(kl)
    const z = databaza.get(kl)
    const fn = (zz) => setStav({ ludia: zz.ludia || [], zakazane: !!zz.zakazane })
    z.subs.add(fn)
    if (z.ludia) fn(z)
    return () => { z.subs.delete(fn) }
  }, [kl, zapnute])
  return stav
}
// Osoba z databázy ako voľba výberu: meno navrchu, pod ním firma/funkcia · číslo · e-mail; hľadá sa aj podľa čísla a e-mailu
export function moznostOsoby(o, hodnota) {
  const tel = (o.t || [])[0], mail = (o.e || [])[0]
  const kto = o.pr === 1 ? 'osoba partnera' : o.pr === 2 ? 'z pošty — doména partnera' : ''
  return {
    value: hodnota != null ? hodnota : 'k:' + o.k,
    label: o.m || mail || pekneTel(tel) || o.f || '—',
    popis: [kto, o.p || o.f, tel ? pekneTel(tel) : '', mail || ''].filter(Boolean).filter((x, i, a) => a.indexOf(x) === i).join(' · '),
    hladat: [o.f, o.p, ...(o.e || []), ...(o.t || []).map(tvaryCisla)].filter(Boolean).join(' '),
  }
}
export const osobaZDatabazy = (o) => ({ meno: o.m || '', funkcia: '', telefon: (o.t || [])[0] ? pekneTel(o.t[0]) : '', email: (o.e || [])[0] || '' })

// p107 — „Uložiť kontakt" (Mirko 22. 9. 2026: „ak napíše niekto SMS niekomu — výzva na uloženie kontaktu"). JEDEN
// komponent (0.J) pre Kontakty (zaradiť osobu bez karty, nový kontakt) aj SMS: vyber partnera → nová osoba partnera.
export function UlozitKontakt({ meno = '', email = '', telefon = '', onUlozene, onZrus, nadpis = 'Ku ktorému partnerovi patrí?', zrusText = 'Zrušiť' }) {
  const [partner, setPartner] = useState('')
  const [partneri, setPartneri] = useState([])
  useEffect(() => { fetchAuth('/api/customers').then(r => (r.ok ? r.json() : [])).then(d => setPartneri(Array.isArray(d) ? d : [])).catch(() => {}) }, [])
  return (
    <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, padding: 10, background: '#fff' }}>
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: TYPO.weights.bold, marginBottom: 4 }}>{nadpis}</div>
      <SearchableSelect value={partner} onChange={v => setPartner(v || '')} placeholder="Vyber partnera…" menuPortal
        options={partneri.map(c => ({ value: String(c.id), label: c.name + (c.ico ? ' · IČO ' + c.ico : '') }))} />
      {partner && <NovyKontaktPartnera key={partner} customerId={partner} meno={meno} email={email} telefon={telefon} onUlozene={onUlozene} onZrus={onZrus} />}
      {!partner && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" onClick={onZrus} style={{ padding: '5px 12px', fontSize: TYPO.sizes.sm, fontFamily: 'inherit', borderRadius: RADIUS.sm, cursor: 'pointer', border: `1px solid ${COLORS.line}`, background: '#fff', color: COLORS.text }}>{zrusText}</button>
          <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Súkromnú osobu (napr. zákazníka RD) založíš v Partneroch — do Kontaktov príde sama.</span>
        </div>
      )}
    </div>
  )
}

// p107 — číslo pre SMS z celej databázy: píšeš meno, firmu alebo číslo; napísané číslo sa použije tak, ako je.
export function VyberCisla({ value, onChange, placeholder = 'Meno, firma alebo číslo…' }) {
  const { ludia, zakazane } = useKontaktyDatabaza('', true)
  const [zaloha, setZaloha] = useState([])   // rola bez Kontaktov → zoznam čísel SMS (/api/sms/kontakty)
  useEffect(() => {
    if (!zakazane) return
    fetchAuth('/api/sms/kontakty').then(r => (r.ok ? r.json() : [])).then(d => setZaloha(Array.isArray(d) ? d : [])).catch(() => {})
  }, [zakazane])
  const moznosti = useMemo(() => {
    const zdroj = zakazane ? zaloha.map(k => ({ k: 'sms:' + k.n, m: k.meno || '', f: '', p: k.typ || '', t: ['421' + k.n], e: [], pr: 0 })) : ludia
    const videne = new Set(), out = []
    for (const o of zdroj) for (const t of (o.t || [])) {
      const h = pekneTel(t); if (!h || videne.has(h)) continue
      videne.add(h); out.push(moznostOsoby({ ...o, t: [t] }, h))
    }
    return out
  }, [ludia, zaloha, zakazane])
  return <SearchableSelect value={value || ''} onChange={v => onChange && onChange(v || '')} options={moznosti} menuPortal allowCreate
    placeholder={placeholder} createLabel={(t) => 'Poslať na číslo „' + t + '"'} />
}

// p107 — pri písaní mena na doklade bez partnera Matrix ponúkne ľudí z databázy; klik doplní meno, telefón a e-mail.
// Enter ponechá napísané meno — nič sa nevyberie samo (ústava 13.1b: Matrix nepriraďuje, rozhodne človek).
function NavrhyOsob({ osoba, ludia, onVyber }) {
  const q = norm(osoba.meno || '')
  if (q.trim().length < 3 || !ludia.length) return null
  const vybrana = (o) => norm(o.m) === q && ((o.e || []).includes(lc(osoba.email)) || (o.t || []).some(t => cislaTel(t) === cislaTel(osoba.telefon) && cislaTel(t).length >= 9))
  const zhody = ludia.filter(o => o.m && norm([o.m, o.f, ...(o.e || [])].join(' ')).includes(q.trim()) && !vybrana(o)).slice(0, 5)
  if (!zhody.length) return null
  return (
    <div role="group" aria-label="Matrix pozná" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4, alignItems: 'center' }}>
      <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Matrix pozná:</span>
      {zhody.map(o => (
        <button key={o.k} type="button" onMouseDown={e => e.preventDefault()} onClick={() => onVyber(o)} title={moznostOsoby(o).popis}
          style={{ padding: '2px 9px', fontSize: TYPO.sizes.xs, fontFamily: 'inherit', borderRadius: 12, cursor: 'pointer', border: `1px solid ${COLORS.line}`, background: '#fff', color: COLORS.text, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {o.m}{o.f && o.f !== o.m ? ' · ' + o.f : ''}
        </button>
      ))}
    </div>
  )
}

// Riadok osôb pod poľom e-mailu: klik na osobu = onVyber(kontakt, uzJeVybrana); „+ Nový kontakt partnera“ otvorí formulár
export function KontaktyPonuka({ customerId, hodnota = '', popis = 'Komu', onVyber, sNovym = true, predvolenyTelefon = '' }) {
  const { rows, pridaj } = usePartnerKontakty(customerId)
  const { ludia } = useKontaktyDatabaza(customerId)   // p107 — celá databáza Matrixu
  const [novy, setNovy] = useState(false)
  const sEmailom = rows.filter(k => EMAIL_OK.test(String(k.email || '').trim()))
  // p107: hľadanie v celej databáze — ľudia s e-mailom, osoby partnera navrchu; osoby z registra partnera sú už v čipoch
  const moznosti = useMemo(() => {
    const zRegistra = new Set(rows.map(k => lc(k.email)).filter(Boolean))
    const out = []
    for (const o of ludia) for (const e of (o.e || [])) if (EMAIL_OK.test(e) && !zRegistra.has(lc(e))) out.push(moznostOsoby({ ...o, e: [e] }, 'k:' + o.k + '|' + e))
    return out
  }, [ludia, rows])
  if (!customerId && !moznosti.length) return null
  if (!sEmailom.length && !sNovym && !moznosti.length) return null
  const aktualny = lc(hodnota)
  const znamy = sEmailom.some(k => lc(k.email) === aktualny)
  const predEmail = !znamy && EMAIL_OK.test(String(hodnota || '').trim()) ? String(hodnota).trim() : ''
  const chip = (on) => ({
    display: 'inline-flex', alignItems: 'center', maxWidth: '100%', padding: '2px 9px', fontSize: TYPO.sizes.xs, fontFamily: 'inherit',
    borderRadius: 12, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    border: `1px solid ${on ? COLORS.nav : COLORS.line}`, background: on ? COLORS.navBg : '#fff', color: on ? COLORS.nav : COLORS.text,
    fontWeight: on ? TYPO.weights.bold : 400,
  })
  return (
    <div style={{ marginTop: 5 }}>
      <div role="group" aria-label={popis + ': kontaktné osoby partnera'} style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {sEmailom.length > 0 && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{popis}:</span>}
        {sEmailom.map(k => {
          const on = !!aktualny && lc(k.email) === aktualny
          return (
            <button key={k.id} type="button" aria-pressed={on} onClick={() => onVyber && onVyber(k, on)}
              title={[k.meno, k.funkcia, k.email, k.telefon].filter(Boolean).join(' · ')} style={chip(on)}>
              {(k.meno || k.email) + (k.funkcia ? ' · ' + k.funkcia : '')}
            </button>
          )
        })}
        {sNovym && customerId && (
          <button type="button" aria-expanded={novy} onClick={() => setNovy(v => !v)}
            style={{ ...chip(false), borderStyle: 'dashed', color: COLORS.add, fontWeight: TYPO.weights.bold }}>
            {novy ? 'Zavrieť nový kontakt' : '+ Nový kontakt partnera'}
          </button>
        )}
      </div>
      {moznosti.length > 0 && (
        <div style={{ marginTop: 4, maxWidth: 460 }}>
          <SearchableSelect value="" menuPortal options={moznosti}
            placeholder={popis + ': hľadať v celom Matrixe — meno, firma, e-mail, číslo…'}
            onChange={v => {
              const [kl, e] = String(v || '').replace(/^k:/, '').split('|')
              const o = ludia.find(x => x.k === kl)
              if (o && onVyber) onVyber({ ...osobaZDatabazy(o), email: e || (o.e || [])[0] || '' }, false)
            }} />
        </div>
      )}
      {novy && customerId && (
        <NovyKontaktPartnera customerId={customerId} email={predEmail} telefon={predEmail ? predvolenyTelefon : ''}
          onUlozene={(row) => { pridaj(row); setNovy(false); if (row.email && onVyber) onVyber(row, false) }}
          onZrus={() => setNovy(false)} />
      )}
    </div>
  )
}

export const FUNKCIE = ['rozpočtár', 'stavbyvedúci', 'majster', 'konateľ', 'nákupca', 'technik', 'účtovníčka']

// KONTAKT_BUNKY_V1 (ústava 139.1 a 139.3; Mirko 21. 10. 2026: „prečo nemá meno a priezvisko ale opäť jedna bunka“): osoba partnera
// = Titul · Meno · Priezvisko, tri bunky vedľa seba, celé meno dopočíta server. Starý kontakt s jedným poľom sa pri úprave
// rozpíše do buniek (titul pred menom, prvé slovo meno, zvyšok priezvisko) a človek to pred uložením vidí a opraví.
const TITUL = /^(ing|mgr|bc|judr|mudr|mvdr|rndr|phdr|paeddr|doc|prof|dr|arch|akad|phd|csc|mba|dis)\.?,?$/i
export const bunkyOsoby = (k) => {
  if (k && (k.first_name || k.last_name)) return { titul: k.titul || '', first_name: k.first_name || '', last_name: k.last_name || '' }
  const slova = String((k && k.meno) || '').trim().split(/\s+/).filter(Boolean), tit = []
  while (slova.length > 1 && TITUL.test(slova[0])) tit.push(slova.shift())
  while (slova.length > 2 && TITUL.test(slova[slova.length - 1])) tit.push(slova.pop())
  return { titul: tit.join(' ').replace(/,$/, ''), first_name: slova.length > 1 ? slova[0] : '', last_name: slova.slice(slova.length > 1 ? 1 : 0).join(' ').replace(/,$/, '') }
}
export const menoZBuniek = (v) => [v.titul, v.first_name, v.last_name].map(x => String(x || '').trim()).filter(Boolean).join(' ')
export function OsobaBunky({ v, set, lbl, inp, autoFocus = false, disabled = false }) {
  return (
    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '84px minmax(0, 1fr) minmax(0, 1fr)', gap: 6 }}>
      <label style={lbl}>Titul<input value={v.titul || ''} disabled={disabled} onChange={e => set('titul', e.target.value)} placeholder="Ing." style={inp} /></label>
      <label style={lbl}>Meno *<input autoFocus={autoFocus} value={v.first_name || ''} disabled={disabled} onChange={e => set('first_name', e.target.value)} style={inp} /></label>
      <label style={lbl}>Priezvisko *<input value={v.last_name || ''} disabled={disabled} onChange={e => set('last_name', e.target.value)} style={inp} /></label>
    </div>
  )
}

// Ručné založenie kontaktnej osoby partnera (meno, funkcia, e-mail, telefón); rovnaký e-mail alebo telefón vráti existujúci kontakt
export function NovyKontaktPartnera({ customerId, meno = '', email = '', telefon = '', onUlozene, onZrus }) {
  const toast = useToast()
  const [f, setF] = useState(() => ({ ...bunkyOsoby({ meno }), funkcia: 'rozpočtár', email, telefon }))
  const [busy, setBusy] = useState(false)
  const [dlId] = useState(() => 'kp-funkcie-' + Math.random().toString(36).slice(2, 8))
  const set = (k, v) => setF(s => ({ ...s, [k]: v }))
  const uloz = async () => {
    if (busy) return
    const meno = menoZBuniek(f), em = f.email.trim(), tel = String(f.telefon || '').trim()
    if (!f.first_name.trim() || !f.last_name.trim()) { toast.error('Zadaj meno aj priezvisko kontaktu'); return }
    if (!em && !tel) { toast.error('Zadaj e-mail alebo telefón'); return }
    if (em && !EMAIL_OK.test(em)) { toast.error('E-mail nie je platný'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/partner-contacts', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customer_id: customerId, titul: f.titul.trim(), first_name: f.first_name.trim(), last_name: f.last_name.trim(), funkcia: f.funkcia.trim(), email: em, telefon: tel }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || 'Uloženie kontaktu zlyhalo')
      toast.success(d.duplicate ? 'Tento e-mail alebo telefón už partner má — vybraný je kontakt ' + (d.meno || d.email || d.telefon || '') : 'Kontakt uložený k partnerovi: ' + meno)
      onUlozene && onUlozene(d)
    } catch (e) { toast.error(e.message) } finally { setBusy(false) }
  }
  const lbl = { display: 'flex', flexDirection: 'column', gap: 2, fontSize: TYPO.sizes.xs, color: COLORS.muted, minWidth: 0 }
  const inp = { padding: '5px 8px', fontSize: TYPO.sizes.sm, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%', background: '#fff', color: COLORS.text }
  const btn = (primary) => ({ padding: '5px 12px', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, fontFamily: 'inherit', borderRadius: RADIUS.sm, cursor: busy ? 'wait' : 'pointer', border: `1px solid ${primary ? COLORS.add : COLORS.line}`, background: primary ? COLORS.add : '#fff', color: primary ? '#fff' : COLORS.text, whiteSpace: 'nowrap' })
  return (
    <div onKeyDown={e => { if (e.key === 'Enter' && e.target && e.target.tagName === 'INPUT') { e.preventDefault(); e.stopPropagation(); uloz() } }}
      style={{ marginTop: 6, padding: 8, border: `1px dashed ${COLORS.add}`, borderRadius: RADIUS.sm, background: COLORS.addBg, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 6, alignItems: 'end' }}>
      <OsobaBunky v={f} set={set} lbl={lbl} inp={inp} autoFocus />
      <label style={lbl}>Funkcia<input list={dlId} value={f.funkcia} onChange={e => set('funkcia', e.target.value)} style={inp} /></label>
      <datalist id={dlId}>{FUNKCIE.map(x => <option key={x} value={x} />)}</datalist>
      <label style={lbl}>E-mail<input type="email" value={f.email} onChange={e => set('email', e.target.value)} style={inp} /></label>
      <label style={lbl}>Telefón<TelefonInput value={f.telefon} onChange={v => set('telefon', v)} style={inp} /></label>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" disabled={busy} onClick={uloz} style={btn(true)}>{busy ? 'Ukladám…' : 'Uložiť kontakt'}</button>
        <button type="button" onClick={onZrus} style={btn(false)}>Zrušiť</button>
      </div>
    </div>
  )
}

// ── p93 — JEDEN blok kontaktnej osoby v celom Matrixe (ústava 131.10 + 0.J) ───────────────────────────────
// Mirko 21. 9. 2026: „celá komercia musí mať spoločný register — celý Matrix" a „Kontaktná osoba, funkcia,
// e-mail, telefón — aby to bolo v Matrixe rovnaké". Tie isté štyri polia na obhliadke, kalkulácii, ponuke,
// objednávke aj poterovom protokole. Osoba sa vyberá z registra partnera; nová osoba sa zakladá do registra,
// nie do dokladu. Doklad si drží kópiu mena, telefónu a e-mailu (tlač, odoslanie, SMS); funkcia sa číta z registra.
// obal(popis, pole, kľúč) = bunka mriežky daného formulára (jeho Field), inp = štýl jeho polí — blok vyzerá ako
// okolie. ulozMeno=false: doklad nemá stĺpec pre meno (CP) — meno ukáže register podľa e-mailu.
export function KontaktnaOsoba({ customerId, osoba = {}, onZmena, obal, inp = {}, zamknute = false,
  ulozMeno = true, ulozFunkciu = false, popisy = {}, placeholdery = {} }) {
  const { rows, pridaj } = usePartnerKontakty(customerId)
  const [novy, setNovy] = useState(null)   // null = zavreté, { meno } = formulár novej osoby partnera
  const cid = String(customerId || '').trim()
  // p107 — CHRBTICA: celá databáza ľudí Matrixu; osoby partnera navrchu, potom všetci ostatní (bez tých, čo už sú v registri)
  const { ludia } = useKontaktyDatabaza(cid, !zamknute)
  const zDatabazy = useMemo(() => {
    if (!cid) return []
    const vRegistri = (o) => rows.some(k => (lc(k.email) && (o.e || []).includes(lc(k.email))) || (cislaTel(k.telefon).length >= 9 && (o.t || []).some(t => cislaTel(t) === cislaTel(k.telefon))))
    return ludia.filter(o => ((o.t || []).length || (o.e || []).length) && !vRegistri(o)).map(o => moznostOsoby(o))
  }, [ludia, rows, cid])
  const L = { osoba: 'Kontaktná osoba', funkcia: 'Funkcia', telefon: 'Telefón', email: 'E-mail', ...popisy }
  const em = lc(osoba.email), tel = cislaTel(osoba.telefon)
  const reg = (em && rows.find(k => lc(k.email) === em)) || (!em && tel.length >= 9 && rows.find(k => cislaTel(k.telefon) === tel)) || null
  const menoDokladu = String(osoba.meno || '').trim()
  // osoba_v2 (8. 10. 2026, ústava 139): meno a priezvisko do dvoch buniek. Držia sa lokálne,
  // aby pri písaní krstného mena neskákal kurzor; z uloženej hodnoty sa prepíšu len vtedy,
  // keď ju zmenil niekto iný — napríklad výber osoby z registra.
  const [krstneMeno, setKrstneMeno] = useState('')
  const [priezviskoOs, setPriezviskoOs] = useState('')
  const naposledyPoslane = useRef(null)
  useEffect(() => {
    const m = String(osoba.meno || '')
    if (m === naposledyPoslane.current) return
    const kusy = m.trim().split(/\s+/).filter(Boolean)
    setPriezviskoOs(kusy.length ? kusy[kusy.length - 1] : '')
    setKrstneMeno(kusy.length > 1 ? kusy.slice(0, -1).join(' ') : '')
  }, [osoba.meno])
  const cudzia = !reg && !!(menoDokladu || em)        // osoba na doklade, ktorú register partnera nepozná
  const funkcia = (reg && reg.funkcia) || osoba.funkcia || ''
  const vyber = (k) => onZmena && onZmena({ meno: k.meno || '', funkcia: k.funkcia || '', telefon: k.telefon || '', email: k.email || '' })
  const zmen = (o) => onZmena && onZmena({ meno: osoba.meno || '', funkcia: osoba.funkcia || '', telefon: osoba.telefon || '', email: osoba.email || '', ...o })
  const ro = { ...inp, background: COLORS.lineSoft, cursor: 'default', lineHeight: 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
  const volba = (v) => {
    if (v === '__novy') { setNovy({ meno: '' }); return }
    if (String(v || '').startsWith('k:')) {
      const o = ludia.find(x => 'k:' + x.k === v)
      if (o) {
        const os = osobaZDatabazy(o)
        vyber(os)
        // p107: výber osoby na doklade partnera = osoba patrí k partnerovi → zapíše sa do registra (131.10.A; rozhodol
        // človek výberom). Zamestnanec KAMIR sa k partnerovi nezapisuje. Rovnakú osobu register nezaloží druhýkrát (13.1b).
        if (cid && os.meno && !/^(zamestnanec|byvaly)/.test(o.d || '') && (os.email || os.telefon)) {
          fetchAuth('/api/partner-contacts', { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ customer_id: cid, meno: os.meno, funkcia: '', email: os.email, telefon: os.telefon }) })
            .then(r => (r.ok ? r.json() : null)).then(row => { if (row && row.id) pridaj(row) }).catch(() => {})
        }
        return
      }
    }
    const k = rows.find(x => String(x.id) === String(v))
    if (k) { vyber(k); return }
    if (!v) { if (reg) vyber({}); return }           // „— bez kontaktnej osoby —"
    setNovy({ meno: String(v).trim() })               // napísané meno, ktoré register nepozná → nová osoba partnera
  }
  const poleOsoba = zamknute
    ? <div style={ro}>{(reg && reg.meno) || menoDokladu || '—'}</div>
    : cid
      ? <SearchableSelect value={reg ? String(reg.id) : ''} onChange={volba} menuPortal allowCreate
          placeholder="Hľadať osobu v celom Matrixe alebo napísať nové meno…"
          emptyLabel={cudzia ? (menoDokladu || osoba.email) + ' (nie je v registri)' : reg ? '— bez kontaktnej osoby —' : '— vyber osobu partnera —'}
          options={[...rows.map(k => ({ value: String(k.id), label: (k.meno || k.email || k.telefon) + (k.funkcia ? ' — ' + k.funkcia : ''),
              popis: ['osoba partnera', k.telefon, k.email].filter(Boolean).join(' · '), hladat: [k.email, tvaryCisla(k.telefon)].filter(Boolean).join(' ') })),
            ...zDatabazy, { value: '__novy', label: '+ Nová osoba partnera…' }]} />
      : ulozMeno
        ? <div style={{ minWidth: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <input style={inp} value={krstneMeno} placeholder={placeholdery.meno || 'meno'}
                onChange={e => { const k = e.target.value; setKrstneMeno(k); const m = [String(k).trim(), String(priezviskoOs).trim()].filter(Boolean).join(' '); naposledyPoslane.current = m; zmen({ meno: m }) }} />
              <input style={inp} value={priezviskoOs} placeholder={placeholdery.priezvisko || 'priezvisko'}
                onChange={e => { const p = e.target.value; setPriezviskoOs(p); const m = [String(krstneMeno).trim(), String(p).trim()].filter(Boolean).join(' '); naposledyPoslane.current = m; zmen({ meno: m }) }} />
            </div>
            <NavrhyOsob osoba={osoba} ludia={ludia} onVyber={(o) => vyber(osobaZDatabazy(o))} />
          </div>
        : <div style={{ ...ro, color: COLORS.muted }} title="Osoby sa berú z registra partnera — najprv vyber partnera">najprv vyber partnera</div>
  const poleFunkcia = (!cid && ulozFunkciu && !zamknute)
    ? <input style={inp} value={osoba.funkcia || ''} onChange={e => zmen({ funkcia: e.target.value })} placeholder="napr. rozpočtár" />
    : <div style={{ ...ro, color: funkcia ? COLORS.text : COLORS.muted }} title="Funkcia sa berie z registra partnera">{funkcia || '—'}</div>
  const poleTelefon = <TelefonInput style={inp} value={osoba.telefon || ''} disabled={zamknute} placeholder={placeholdery.telefon} onChange={v => zmen({ telefon: v })} />
  const poleEmail = <input style={inp} type="email" value={osoba.email || ''} disabled={zamknute} placeholder={placeholdery.email || ''} onChange={e => zmen({ email: e.target.value })} />
  const bunka = obal || ((t, c, k) => <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{t}{c}</label>)
  return (
    <>
      {bunka(L.osoba, poleOsoba, 'ko-osoba')}
      {bunka(L.funkcia, poleFunkcia, 'ko-funkcia')}
      {bunka(L.telefon, poleTelefon, 'ko-telefon')}
      {bunka(L.email, poleEmail, 'ko-email')}
      {novy && cid && !zamknute && (
        <div key="ko-novy" style={{ gridColumn: '1 / -1' }}>
          <NovyKontaktPartnera customerId={cid} meno={novy.meno || (cudzia ? menoDokladu : '')}
            email={reg ? '' : (osoba.email || '')} telefon={reg ? '' : (osoba.telefon || '')}
            onUlozene={(row) => { pridaj(row); setNovy(null); vyber(row) }} onZrus={() => setNovy(null)} />
        </div>
      )}
    </>
  )
}
